import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/lib/auth/store";

/**
 * Single axios instance for the whole admin portal. The access token
 * lives in-memory only (Zustand store). The refresh token is the only
 * piece persisted to localStorage so a hard reload can re-hydrate.
 */
const baseURL = import.meta.env.VITE_GATEWAY_URL || "/";

export const api = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  // Correlation id makes debugging cross-service flows trivial — the
  // backend forwards it through every log line + audit row.
  config.headers.set("X-Correlation-Id", crypto.randomUUID());
  return config;
});

// Refresh-token flight: ensure only one refresh runs at a time even if
// multiple 401s land in parallel.
let refreshFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshFlight) return refreshFlight;
  refreshFlight = (async () => {
    try {
      const refresh = useAuthStore.getState().refreshToken;
      if (!refresh) return null;
      const res = await axios.post(
        `${baseURL.replace(/\/$/, "")}/api/v1/auth/token/refresh`,
        null,
        { headers: { "X-Refresh-Token": refresh } },
      );
      if (res.data?.code === "AUTH-20" && res.data?.data?.accessToken) {
        const { accessToken, refreshToken } = res.data.data;
        useAuthStore.getState().setTokens(accessToken, refreshToken ?? refresh);
        return accessToken as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshFlight = null;
    }
  })();
  return refreshFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean;
    };
    // Backend always returns HTTP 200 + envelope. Real 401s indicate
    // edge-level auth rejection from spring-security / api-gateway.
    if (error.response?.status === 401 && !original._retried) {
      original._retried = true;
      const next = await refreshAccessToken();
      if (next) {
        original.headers!.set("Authorization", `Bearer ${next}`);
        return api(original);
      }
      useAuthStore.getState().clear();
      window.location.assign("/login?reason=expired");
    }
    return Promise.reject(error);
  },
);
